import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";
import "./styles/LeagueJoinPage.css";

const K_BLUE = "#0D47A1";
const K_YELLOW = "#FFF70D";

const LeagueJoinPage = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  React.useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const userId = useMemo(() => {
    if (!user) return null;
    if (typeof user === "string" || typeof user === "number") return user;
    return user.userId ?? user.id ?? null;
  }, [user]);

  const validationError = useMemo(() => {
    const value = name.trim();
    if (!value) return t("leagueJoin.validation.required");
    if (value.length < 3) return t("leagueJoin.validation.min");
    if (value.length > 40) return t("leagueJoin.validation.max");
    return null;
  }, [name, t]);

  const parseJsonSafe = async (res) => {
    try {
      const text = await res.text();
      if (!text || text.trimLeft().startsWith("<")) return {};
      return JSON.parse(text);
    } catch {
      return {};
    }
  };

  const joinLeague = async () => {
    if (submitting) return;
    setError(null);

    const vErr = validationError;
    if (vErr) {
      setError(vErr);
      return;
    }

    setSubmitting(true);

    try {
      const payload = { name: name.trim() };
      if (userId != null) payload.userId = userId;

      const res = await fetch(`${apiUrl}/joinLeague`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await parseJsonSafe(res);

      const msg = (body.message ?? "").toString().toLowerCase();
      const code = (body.code ?? "").toString();

      const leagueNotFound =
        res.status === 404 ||
        code === "LEAGUE_NOT_FOUND" ||
        msg.includes("n'existe pas") ||
        msg.includes("n’existe pas") ||
        msg.includes("not found");

      if (leagueNotFound) {
        setSubmitting(false);
        setError(t("leagueJoin.errors.notFound"));
        return;
      }

      if (res.status === 200 || res.status === 201) {
        setSubmitting(false);
        setSuccessOpen(true);
        return;
      }

      throw new Error(body.message ?? `HTTP ${res.status}`);
    } catch (e) {
      setSubmitting(false);
      setError(t("leagueJoin.errors.generic", { message: e?.message ?? String(e) }));
    }
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    joinLeague();
  };

  const closeSuccess = () => {
    setSuccessOpen(false);
    navigate("/profil");
  };

  return (
    <div className="lj-page">
      <div className="lj-topbar">
        <button
          className="lj-back"
          onClick={() => navigate(-1)}
          aria-label={t("leagueJoin.back")}
        >
          ←
        </button>
        <div className="lj-title">{t("leagueJoin.title")}</div>
        <div style={{ width: 34 }} />
      </div>

      <div className="lj-content">
        <form className="lj-form" onSubmit={onSubmitForm}>
          <div className="lj-section-title">
            <div className="lj-yellow-bar" />
            <span>{t("leagueJoin.sectionTitle")}</span>
          </div>

          <label className="lj-label" htmlFor="leagueName">
            {t("leagueJoin.form.leagueNameLabel")}
          </label>

          <input
            id="leagueName"
            className="lj-input"
            placeholder={t("leagueJoin.form.placeholder")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            maxLength={40}
            autoComplete="off"
          />

          {error ? (
            <>
              <div className="lj-error">{error}</div>

              <button
                type="button"
                className="lj-create"
                onClick={() => navigate("/leagues/create")}
              >
                {t("leagueJoin.actions.createThisLeague")}
              </button>
            </>
          ) : null}

          <button className="lj-submit" type="submit" disabled={submitting}>
            {submitting ? t("leagueJoin.actions.joining") : t("leagueJoin.actions.join")}
          </button>
        </form>
      </div>

      {successOpen ? (
        <div className="lj-modal-overlay" role="dialog" aria-modal="true">
          <div className="lj-modal">
            <div className="lj-modal-header">
              <div className="lj-modal-header-left">
                <span className="lj-icon" aria-hidden="true">
                  👥
                </span>
                <span>{t("leagueJoin.success.title")}</span>
              </div>
            </div>

            <div className="lj-modal-body">
              {t("leagueJoin.success.messageStart")}{" "}
              <b>{name.trim()}</b>
              {t("leagueJoin.success.messageEnd")}
            </div>

            <div className="lj-modal-actions">
              <button className="lj-modal-ok" onClick={closeSuccess}>
                {t("leagueJoin.success.ok")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        :root{
          --lj-blue:${K_BLUE};
          --lj-yellow:${K_YELLOW};
        }
      `}</style>
    </div>
  );
};

export default LeagueJoinPage;
